import { FunctionComponent, useState } from 'react';
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';
import SafeLocalStorage from '~/helper/SafeLocalStorage';
import useElementViewed from '~/lib/segment/useElementViewed';
import styles from './HoveringStickyMobileCTA.module.scss';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { WALMART_URLS } from '../../../config/urls';
import { useRouter } from 'next/router';

interface HoveringStickyMobileCTAProps {
  /**
   * CTA Button text
   *
   * @inner
   * @optional
   */
  CTAButtonText?: string;

  /**
   * CTA Button variant
   *
   * @inner
   * @optional
   */
  CTAButtonVariant?: CTAColorVariant;

  /**
   * CTA Button link
   *
   * @inner
   * @optional
   * */
  CTAButtonLink?: string;
}

const DEFAULT_PROPS = {
  CTAButtonText: 'Claim your free trial',
  CTAButtonVariant: 'aa-primary-blue' as CTAColorVariant,
  CTAButtonLink: 'https://www.walmart.com/plus/plans',
};

const STUDENT_PROPS = {
  CTAButtonText: 'Claim your free trial',
  CTAButtonVariant: 'aa-primary-blue' as CTAColorVariant,
  CTAButtonLink: WALMART_URLS.PLUS_STUDENT_SIGNUP,
};

const HoveringStickyMobileCTA: FunctionComponent<
  HoveringStickyMobileCTAProps
> = (props) => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const router = useRouter();
  const isStudentPage = router.pathname.startsWith('/student');
  const baseProps = isStudentPage ? STUDENT_PROPS : DEFAULT_PROPS;
  const mergedData = {
    ...baseProps,
    ...data,
    ...props,
  };
  const { CTAButtonText, CTAButtonVariant, CTAButtonLink } = mergedData;

  const [isVisible, setIsVisible] = useState(false);

  window.addEventListener('scroll', () => {
    if (document.getElementById('trigger-hov-cta')) {
      const scrollElement = document.getElementById('trigger-hov-cta');

      // returns the current position below the hero relative to the viewport
      const belowHeroPosition = scrollElement?.getBoundingClientRect();
      const scrollPosition = window.innerHeight;
      if (belowHeroPosition && belowHeroPosition.bottom <= scrollPosition) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }
  });

  // determines if bottom sticky should appear on page reload
  window.addEventListener('load', () => {
    const scrollPosition: number | undefined =
      SafeLocalStorage.getItem('scrollPosition');
    if (scrollPosition && scrollPosition >= 700) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  });

  // before the page is reloaded, store the current scroll position
  window.addEventListener('beforeunload', () => {
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;
    SafeLocalStorage.setItem('scrollPosition', scrollPosition);
  });

  const hoverCTARef = useElementViewed();

  return (
    <div
      className={isVisible ? styles.CTAWrapperFadeIn : styles.CTAWrapperFadeOut}
    >
      {isVisible && (
        <span
          ref={isVisible ? hoverCTARef : null}
          data-element-position='BOTTOM_STICKY_SECTION_IN_VIEW'
          data-element-name='HOVER_CTA'
        >
          <CTAButton
            className={styles.button}
            variant={CTAButtonVariant}
            size='aa'
            elementPosition='STICKY_FOOTER_SIGNUP_CTA'
            elementType='SIGN_UP_CTA_PRIMARY'
            href={CTAButtonLink}
          >
            {CTAButtonText}
          </CTAButton>
        </span>
      )}
    </div>
  );
};

export default HoveringStickyMobileCTA;
